import React, { useState, useEffect, useRef } from "react";
import { usePopper } from "react-popper";
import CountryCodeSelect from "../form_fields/country_code_select.js";
import Cleave from "cleave.js/react";

const EmergencyContactNumberInput = ({
  formDataPhoneNumber = "",
  countryCodeDropdownChanged,
  handlePhoneChange,
  checkInput,
}) => {
  const [countryCode, setCountryCode] = useState("");
  const [emergencyContactNumber, setEmergencyContactNumber] = useState("");
  const [blurred, setBlurred] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const errorMessage =
    "Emergency contact phone number must include at least 10 digits.";

  const inputRef = useRef(null);
  const tooltipRef = useRef(null);

  // Set up Popper for the tooltip
  const { styles, attributes } = usePopper(inputRef.current, tooltipRef.current, {
    placement: "right",
    modifiers: [
      { name: "offset", options: { offset: [0, 8] } },
      { name: "preventOverflow", options: { padding: 8 } },
    ],
  });

  // Initialize fields if we have a stored phone string like "+1 1234567890"
  useEffect(() => {
    if (formDataPhoneNumber.trim() !== "") {
      const [code, number] = formDataPhoneNumber.split(" ");
      if (code) {
        setCountryCode(code);
      }
      setEmergencyContactNumber(number || "");
    }
  }, [formDataPhoneNumber]);

  // Evaluate the phone number for errors
  const hasError =
    emergencyContactNumber.replace(/[^0-9]/g, "").length < 10 ||
    (countryCode.replace(/[^0-9]/g, "") +
      emergencyContactNumber.replace(/[^0-9]/g, "")).length > 20;

  // Wrap the user-provided handle to manage local state
  const modifiedHandlePhoneChange = (event) => {
    const updatedNumber = event.target.rawValue;
    // If combined length (country + number) <= 20, allow the input
    if (
      (countryCode.replace(/[^0-9]/g, "") + updatedNumber).length <= 20
    ) {
      setEmergencyContactNumber(updatedNumber);
    }
    // Also call the parent's handler if provided
    handlePhoneChange?.(event);
  };

  const onCountryCodeChange = (newValue) => {
    setCountryCode(newValue);
    countryCodeDropdownChanged?.({
      target: { value: newValue },
    });
  };

  const onBlurHandler = (event) => {
    setBlurred(true);

    // If there's an error, show the tooltip
    setTooltipVisible(hasError);

    checkInput?.(event);
  };

  // A small helper to dismiss the tooltip
  const closeTooltip = () => {
    setTooltipVisible(false);
  };

  return (
    <div
      className="emergency-contact-row"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem", // spacing between the two fields
      }}
    >
      {/* Country code selection */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <CountryCodeSelect
          countryCode={countryCode}
          onChange={onCountryCodeChange}
        />
      </div>

      {/* Emergency contact number input */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label htmlFor="emergency_contact_number" style={{ marginBottom: "0.25rem" }}>
          Emergency Contact Number
        </label>
        <Cleave
          id="phone-mask-ecn"
          ref={inputRef}
          className="ecn phone-us field"
          type="tel"
          name="emergency_contact_number"
          value={emergencyContactNumber}
          placeholder="Emergency Contact #"
          options={{ blocks: [3, 3, 4], delimiter: "-", numericOnly: true }}
          onChange={modifiedHandlePhoneChange}
          onBlur={onBlurHandler}
          aria-invalid={blurred && hasError}
          aria-describedby={blurred && hasError ? "phoneError" : undefined}
        />
      </div>

      {tooltipVisible && blurred && hasError && (
        <div
          ref={tooltipRef}
          id="phoneError"
          role="alert"
          aria-live="assertive"
          style={{
            ...styles.popper,
            background: "rgba(255, 0, 0, 0.9)",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "0.875rem",
            maxWidth: "200px",
            right: '0',
            zIndex: 9999,
          }}
          {...attributes.popper}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{errorMessage}</span>
            {/* Close button to dismiss the tooltip */}
            <button
              onClick={closeTooltip}
              style={{
                marginLeft: "0.5rem",
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyContactNumberInput;
